import { QueryClient, type QueryClientConfig } from '@tanstack/vue-query'

const SECOND = 1000
const MINUTE = 60 * SECOND

/**
 * Default configuration for the QueryClient.
 *
 * These defaults are designed for production use:
 * - staleTime: 1 minute - prevents excessive refetching
 * - gcTime: 5 minutes - keeps inactive data available for quick navigation
 * - retry: 3 attempts with exponential backoff
 * - refetchOnWindowFocus: only for stale queries (default behavior)
 */
export const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 1 * MINUTE,
      gcTime: 5 * MINUTE,
      retry: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
    mutations: {
      retry: false,
    },
  },
}

/**
 * Creates a new QueryClient instance with the default configuration.
 * Use this when you need to create the client (e.g., in app initialization).
 */
export function createQueryClient(config?: Partial<QueryClientConfig>): QueryClient {
  return new QueryClient({
    ...queryClientConfig,
    ...config,
    defaultOptions: {
      ...queryClientConfig.defaultOptions,
      ...config?.defaultOptions,
      queries: {
        ...queryClientConfig.defaultOptions?.queries,
        ...config?.defaultOptions?.queries,
      },
      mutations: {
        ...queryClientConfig.defaultOptions?.mutations,
        ...config?.defaultOptions?.mutations,
      },
    },
  })
}

/**
 * Singleton QueryClient instance for the application.
 * Export this when you need to access the client outside of Vue components
 * (e.g., in route loaders, utility functions, or tests).
 */
export const queryClient = createQueryClient()
